import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus, Search, Trash2, UserPlus } from 'lucide-react';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';

const LICITATION_STATUS_STYLES = {
  Abierta: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  'En Analisis': { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  'Oferta Enviada': { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
  Adjudicada: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Perdida: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  Cancelada: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'Abierta', label: 'Abierta' },
  { value: 'En Analisis', label: 'En Análisis' },
  { value: 'Oferta Enviada', label: 'Oferta Enviada' },
  { value: 'Adjudicada', label: 'Adjudicada' },
  { value: 'Perdida', label: 'Perdida' },
  { value: 'Cancelada', label: 'Cancelada' },
];

const BULK_STATUS_OPTIONS = [
  { value: 'Abierta', label: 'Abierta' },
  { value: 'En Analisis', label: 'En Análisis' },
  { value: 'Oferta Enviada', label: 'Oferta Enviada' },
  { value: 'Adjudicada', label: 'Adjudicada' },
  { value: 'Perdida', label: 'Perdida' },
  { value: 'Cancelada', label: 'Cancelada' },
];

/** Colores de canal (mismos que cotizaciones). */
const CHANNEL_COLORS = {
  Directo: '#4f46e5',
  'M. Público - Compra Ágil': '#2563eb',
  'M. Público - Licitación': '#0284c7',
  'M. Público - Trato Directo': '#7c3aed',
  Senegocia: '#d97706',
  WhereX: '#8b5cf6',
};

const getChannelColor = (channel, fallbackMuted) => {
  const key = String(channel || '').trim();
  if (!key) return fallbackMuted;
  if (CHANNEL_COLORS[key]) return CHANNEL_COLORS[key];
  const lower = key.toLowerCase();
  const match = Object.keys(CHANNEL_COLORS).find((k) => k.toLowerCase() === lower);
  return match ? CHANNEL_COLORS[match] : '#6366f1';
};

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'licitation_code' },
  { key: 'Licitación', align: 'left', sortKey: 'tender_name' },
  { key: 'Organización', align: 'left', sortKey: 'organization_name' },
  { key: 'Canal', align: 'left', sortKey: 'channel' },
  { key: 'Fechas', align: 'left', sortKey: 'closing_date' },
  { key: 'Monto máximo', align: 'right', sortKey: 'maximum_amount' },
  { key: 'Monto ganador', align: 'right', sortKey: 'winning_amount' },
  { key: 'Ganador', align: 'left', sortKey: 'winner' },
  { key: 'Responsable', align: 'left', sortKey: 'assigned_user' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const formatDateEs = (iso) => {
  if (!iso) return '—';
  const raw = String(iso).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value, currency = 'CLP') => {
  const n = Number(value || 0);
  const sym = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : '$';
  return `${sym} ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'licitation_code':
      return String(row.licitation_code || '').toLowerCase();
    case 'tender_code':
      return String(row.tender_code || '').toLowerCase();
    case 'tender_name':
      return String(row.tender_name || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'channel':
      return String(row.channel || '').toLowerCase();
    case 'closing_date':
      return String(row.closing_date || '');
    case 'adjudication_date':
      return String(row.adjudication_date || '');
    case 'estimated_amount':
      return Number(row.estimated_amount) || 0;
    case 'maximum_amount':
      return Number(row.maximum_amount) || 0;
    case 'winning_amount':
      return Number(row.winning_amount) || 0;
    case 'winner':
      return String(row.winner || '').toLowerCase();
    case 'assigned_user':
      return String(row.assigned_user || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (sortKey === 'closing_date' || sortKey === 'adjudication_date') {
    const aEmpty = !String(va || '').trim();
    const bEmpty = !String(vb || '').trim();
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
  }
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de licitaciones con el mismo look & feel que QuotationsTable3.
 */
const LicitationsTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  onAdd,
  onRowOpen,
  onRowEdit,
  onBulkDelete,
  onBulkAssign,
  userOptions = [],
  channelOptions = [],
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [sortColumn, setSortColumn] = useState('closing_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserName, setAssignUserName] = useState('');
  const [assignChannel, setAssignChannel] = useState('');
  const [assignStatus, setAssignStatus] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete || onBulkAssign || onRowOpen || onRowEdit);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const filteredRows = useMemo(() => {
    let list = rows;

    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.licitation_code,
          row.tender_code,
          row.tender_name,
          row.organization_name,
          row.channel,
          row.closing_date,
          row.adjudication_date,
          row.estimated_amount,
          row.maximum_amount,
          row.winning_amount,
          row.winner,
          row.currency,
          row.assigned_user,
          row.status,
          row.notes,
          row.id,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }

    if (filterStatus) {
      list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    }

    if (filterChannel === '__none__') {
      list = list.filter((row) => !String(row.channel || '').trim());
    } else if (filterChannel) {
      list = list.filter((row) => String(row.channel || '').trim() === filterChannel);
    }

    return list;
  }, [rows, searchQuery, filterStatus, filterChannel]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );

  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleRowSelected = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.includes(Number(r.id))),
    [rows, selectedIds]
  );

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Eliminar ${n} licitación${n === 1 ? '' : 'es'} seleccionada${n === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    setBulkPending(true);
    try {
      await onBulkDelete(selectedRows);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleEditSelected = () => {
    const openEdit = onRowEdit || onRowOpen;
    if (!openEdit || selectedIds.length !== 1) return;
    const row = selectedRows[0];
    if (row) openEdit(row);
  };

  const openAssignModal = () => {
    if (!onBulkAssign || selectedIds.length === 0) return;
    setAssignUserName('');
    setAssignChannel('');
    setAssignStatus('');
    setShowAssignModal(true);
  };

  const handleConfirmAssign = async () => {
    if (!onBulkAssign || selectedIds.length === 0) return;
    const payload = { licitation_ids: selectedIds };
    if (assignUserName) payload.assigned_user = assignUserName;
    if (assignChannel) payload.channel = assignChannel;
    if (assignStatus) payload.status = assignStatus;
    if (!('assigned_user' in payload) && !('channel' in payload) && !payload.status) {
      window.alert('Selecciona al menos un campo para asignar (responsable, canal o estado).');
      return;
    }
    setBulkPending(true);
    try {
      await onBulkAssign(payload);
      setShowAssignModal(false);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo asignar.');
    } finally {
      setBulkPending(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterChannel('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || Boolean(filterStatus) || Boolean(filterChannel);
  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros · A009_licitations`
        : `${rows.length} registros · A009_licitations`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '180px',
    outline: 'none',
  };

  const SortIcon = ({ active, dir }) => {
    if (!active) return <ArrowUpDown size={12} style={{ opacity: 0.45 }} aria-hidden />;
    return dir === 'asc' ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden />;
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>Licitaciones</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva licitación
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, nombre, organización, responsable…"
              style={{ ...selectStyle, flex: 1, minWidth: 0 }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Canal
          </span>
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos los canales</option>
            <option value="__none__">Sin canal</option>
            {(channelOptions || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#f5f0e8',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '13px', color: text, marginRight: '4px' }}>
            {selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}
          </div>
          {onBulkDelete ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleBulkDelete}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#dc2626',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Trash2 size={14} aria-hidden />
              Eliminar
            </button>
          ) : null}
          {onRowEdit || onRowOpen ? (
            <button
              type="button"
              disabled={bulkPending || selectedIds.length !== 1}
              onClick={handleEditSelected}
              title={selectedIds.length !== 1 ? 'Selecciona una sola fila para editar' : 'Editar'}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: selectedIds.length === 1 ? '#d97706' : '#9ca3af',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending || selectedIds.length !== 1 ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Pencil size={14} aria-hidden />
              Editar
            </button>
          ) : null}
          {onBulkAssign ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={openAssignModal}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <UserPlus size={14} aria-hidden />
              Asignar
            </button>
          ) : null}
          <button
            type="button"
            disabled={bulkPending}
            onClick={clearSelection}
            style={{
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: bulkPending ? 'not-allowed' : 'pointer',
              marginLeft: 'auto',
            }}
          >
            Quitar selección
          </button>
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1100px' }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {showBulkChrome ? (
                <th
                  scope="col"
                  style={{
                    width: '40px',
                    padding: `${thPadV}px 6px`,
                    borderBottom: `1px solid ${border}`,
                    verticalAlign: 'middle',
                  }}
                >
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={loading || sortedRows.length === 0 || bulkPending}
                    aria-label="Seleccionar todas las visibles"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    borderBottom: `1px solid ${border}`,
                    textAlign: align || 'left',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: muted,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(sk)}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      justifyContent:
                        align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
                      width: '100%',
                    }}
                  >
                    {h}
                    <SortIcon active={sortColumn === sk} dir={sortDir} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando licitaciones…
                </td>
              </tr>
            ) : error && rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  No hay licitaciones para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const rowId = Number(row.id);
                const rowSelected = showBulkChrome && selectedIds.includes(rowId);
                const status = String(row.status || 'Abierta').trim() || 'Abierta';
                const palette = LICITATION_STATUS_STYLES[status] || LICITATION_STATUS_STYLES.Abierta;
                return (
                  <tr
                    key={row.id}
                    style={{
                      background: rowSelected ? (isDarkMode ? '#292524' : '#fef3c7') : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!rowSelected) e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = rowSelected
                        ? isDarkMode
                          ? '#292524'
                          : '#fef3c7'
                        : 'transparent';
                    }}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px 6px`, verticalAlign: 'middle', borderBottom: `1px solid ${border}` }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={rowSelected}
                          disabled={bulkPending}
                          onChange={() => toggleRowSelected(rowId)}
                          aria-label={`Seleccionar ${row.licitation_code || row.tender_code || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '160px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => onRowOpen?.(row)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: onRowOpen ? 'pointer' : 'default',
                          textAlign: 'left',
                          fontWeight: 700,
                          fontSize: '12px',
                          color: '#2563eb',
                          lineHeight: 1.2,
                        }}
                      >
                        {row.licitation_code || '—'}
                      </button>
                      {row.tender_code ? (
                        <div
                          style={{
                            fontSize: '11px',
                            color: muted,
                            marginTop: '2px',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={row.tender_code}
                        >
                          {row.tender_code}
                        </div>
                      ) : null}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '240px',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.tender_name || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '220px',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.organization_name || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '160px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: getChannelColor(row.channel, muted),
                          lineHeight: 1.2,
                        }}
                      >
                        {row.channel?.trim() || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontSize: '12px', color: text, lineHeight: 1.2 }}>
                        Cierre: {formatDateEs(row.closing_date)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#ea580c', lineHeight: 1.2 }}>
                        {formatMoney(row.maximum_amount, row.currency)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#16a34a', lineHeight: 1.2 }}>
                        {row.winning_amount == null ? '—' : formatMoney(row.winning_amount, row.currency)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '140px',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 600, color: row.winner ? text : muted }}>
                        {row.winner || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        maxWidth: '160px',
                      }}
                    >
                      <div style={{ fontSize: '12px', color: row.assigned_user ? text : muted, lineHeight: 1.2 }}>
                        {row.assigned_user || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        borderBottom: `1px solid ${border}`,
                        textAlign: 'center',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '10px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          background: palette.bg,
                          color: palette.text,
                          border: `1px solid ${palette.border}`,
                          lineHeight: 1.25,
                        }}
                      >
                        {status}
                      </span>
                      <div
                        style={{
                          fontSize: '11px',
                          color: muted,
                          marginTop: '4px',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDateEs(row.adjudication_date)}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={showAssignModal}
        onClose={() => {
          if (!bulkPending) setShowAssignModal(false);
        }}
        title="Asignar a licitaciones"
        subtitle={
          selectedIds.length === 1
            ? 'Asigna responsable, canal o estado a la licitación seleccionada. Deja en vacío lo que no quieras cambiar.'
            : `Asigna los mismos valores a ${selectedIds.length} licitaciones. Deja en vacío lo que no quieras cambiar.`
        }
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Responsable</span>
            <select
              value={assignUserName}
              onChange={(e) => setAssignUserName(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {(userOptions || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Canal</span>
            <select
              value={assignChannel}
              onChange={(e) => setAssignChannel(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {(channelOptions || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={assignStatus}
              onChange={(e) => setAssignStatus(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {BULK_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '6px' }}>
            <button
              type="button"
              disabled={bulkPending}
              onClick={() => setShowAssignModal(false)}
              style={{
                height: '36px',
                padding: '0 14px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleConfirmAssign}
              style={{
                height: '36px',
                padding: '0 14px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              {bulkPending ? 'Asignando…' : 'Confirmar'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LicitationsTable3;
